"use client";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import COFormV3, { countUnmapped } from "./COFormV3";
import CODetailV3, { StatusBadge } from "./CODetailV3";
import { useParams, useNavigate, useLocation } from "react-router-dom";
const API = "https://erp-xe9u.onrender.com/api";
const cls = (...c) => c.filter(Boolean).join(" ");

const fmtC = (n) => {
  if (!Number.isFinite(n)) return "$0.00";
  const abs = Math.abs(n);
  const s = abs.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${s})` : `$${s}`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return "—";
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ── Toast ──────────────────────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((type, message) => {
    const id = Date.now() + Math.random();
    setToasts((p) => [...p, { id, type, message }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 4500);
  }, []);
  const remove = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  return { toasts, toast, remove };
}

function Toast({ toasts, remove }) {
  return (
    <div className="fixed top-4 right-4 z-[99999] space-y-2 pointer-events-none">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cls(
            "pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl border text-sm font-medium min-w-[280px]",
            t.type === "success"
              ? "bg-green-900 border-green-700 text-green-200"
              : t.type === "error"
                ? "bg-red-900 border-red-700 text-red-200"
                : "bg-gray-800 border-gray-600 text-gray-200",
          )}
        >
          <span className="flex-1">{t.message}</span>
          <button
            onClick={() => remove(t.id)}
            className="text-gray-400 hover:text-white text-lg leading-none"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function Confirm({
  open,
  title,
  message,
  label,
  color = "red",
  onOk,
  onClose,
}) {
  if (!open) return null;
  const c = {
    orange: "bg-orange-500 hover:bg-orange-600",
    green: "bg-green-600 hover:bg-green-700",
    red: "bg-red-600 hover:bg-red-700",
  };
  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 border border-gray-600 rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
        <h3 className="text-base font-semibold text-orange-400 mb-2">
          {title}
        </h3>
        <p className="text-sm text-gray-300 mb-5 whitespace-pre-line">
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onOk}
            className={cls(
              "px-4 py-2 text-sm text-white rounded-lg font-semibold",
              c[color],
            )}
          >
            {label}
          </button>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, color = "text-white", str }) {
  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700/60 px-5 py-4 flex flex-col gap-1">
      <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">
        {label}
      </p>
      <p className={cls("text-xl font-bold tabular-nums", color)}>
        {str ? value : fmtC(value)}
      </p>
    </div>
  );
}

function PageHeader({ onBack, backLabel, title }) {
  return (
    <div className="flex items-center gap-3 pb-3 border-b border-gray-700/50">
      <button
        onClick={onBack}
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium 
                   bg-gray-700 hover:bg-gray-600 
                   text-orange-400 hover:text-orange-300 
                   transition-all duration-200 
                   rounded-2xl active:scale-95 shadow-sm"
      >
        ← {backLabel}
      </button>
      <span className="text-gray-700">|</span>
      <h2 className="text-lg font-semibold text-gray-300">{title}</h2>
    </div>
  );
}

const spinner = (
  <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />{" "}
    Loading…
  </div>
);

// ── Sort helpers ───────────────────────────────────────────────────────────
function SortIcon({ field, sortField, sortOrder }) {
  if (sortField !== field)
    return <span className="ml-1 text-gray-600 text-[10px]">↕</span>;
  return (
    <span className="ml-1 text-orange-400 text-[10px]">
      {sortOrder === "asc" ? "↑" : "↓"}
    </span>
  );
}

function Th({ label, field, sortField, sortOrder, onSort, className = "" }) {
  return (
    <th
      onClick={() => onSort(field)}
      className={cls(
        "px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap hover:text-gray-200 transition-colors",
        className,
      )}
    >
      {label}
      <SortIcon field={field} sortField={sortField} sortOrder={sortOrder} />
    </th>
  );
}

export default function ChangeOrderV3Tab({ project }) {
  const projectId = project?._id;
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };
  const { toasts, toast, remove } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Legacy CO documents predating the billingMode field only have the old
  // `billable` boolean (or nothing at all — schema default is "billable").
  // Treat a missing billingMode the same way the backend schema default
  // would once the document is re-saved, so old approved/pending COs don't
  // silently fall out of filters or show the wrong badge.
  const resolveBillingMode = (co) => {
    if (co.billingMode) return co.billingMode;
    return co.billable === false ? "nonBillable" : "billable";
  };

  const searchParams = new URLSearchParams(location.search);
  const coView = searchParams.get("coView") || "list";
  const coId = searchParams.get("coId") || null;
  const view = coView;

  const [budgetItemMap, setBudgetItemMap] = useState({});
  const [cos, setCOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pos, setPOs] = useState([]);
  const [wos, setWOs] = useState([]);
  const [dbItems, setDbItems] = useState([]);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [contractLabourRates, setContractLabourRates] = useState([]);
  const [selectedCO, setSelectedCO] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const STATUS_FILTERS = ["all", "Pending", "Approved", "Rejected"];
  const [statusFilter, setStatusFilter] = useState("all");
  // "all" | "billable" | "separate" | "nonBillable"
  const [billingModeFilter, setBillingModeFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Sorting
  const [sortField, setSortField] = useState("changeOrderNumber");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortOrder("asc");
      return field;
    });
  }, []);

  const setCoNav = useCallback(
    (newView, id = null) => {
      const p = new URLSearchParams(location.search);
      p.set("tab", "Change Order (CO)");
      if (newView === "list") {
        p.delete("coView");
        p.delete("coId");
      } else {
        p.set("coView", newView);
        if (id) p.set("coId", id);
        else p.delete("coId");
      }
      navigate(`?${p.toString()}`, { replace: true });
    },
    [location.search, navigate],
  );

  const loadCOs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/changeOrders3/project/${projectId}`, {
        headers,
      });
      setCOs(r.data.changeOrders || []);
    } catch {
      toast("error", "Failed to load change orders");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const optsLoadedRef = useRef(false);

  const loadOpts = useCallback(async () => {
    if (optsLoadedRef.current) return;
    optsLoadedRef.current = true; // set before the async call to prevent re-entry
    setLoadingOpts(true);
    try {
      const [poR, dbR, cpR, clrR, biMapR] = await Promise.all([
        axios.get(`${API}/changeOrders3/project/${projectId}/po-wo-options`, {
          headers,
        }),
        axios.get(`${API}/item-database?limit=5000`, { headers }),
        axios.get(`${API}/company-profile`, { headers }),
        axios
          .get(`${API}/projects/${projectId}/contract-labour-items`, {
            headers,
          })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API}/changeOrders3/project/${projectId}/budget-item-map`, {
            headers,
          })
          .catch(() => ({ data: { map: {} } })),
      ]);
      setContractLabourRates(clrR.data || []);
      setPOs(poR.data.purchaseOrders || []);
      setWOs(poR.data.workOrders || []);
      setDbItems(dbR.data.items || []);
      setCompanyProfile(cpR.data || null);
      setBudgetItemMap(biMapR.data.map || {});
    } catch {
      optsLoadedRef.current = false; // allow retry on failure
    } finally {
      setLoadingOpts(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadCOs();
  }, [loadCOs]);
  useEffect(() => {
    if (view !== "list") loadOpts();
  }, [view, loadOpts]);

  useEffect(() => {
    if (!coId) return;
    if (selectedCO?._id === coId) return;
    axios
      .get(`${API}/changeOrders3/${coId}`, { headers })
      .then((r) => setSelectedCO(r.data.changeOrder))
      .catch(() => setCoNav("list"));
  }, [coId]);

  const loadDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/changeOrders3/${id}`, { headers });
      const co = r.data.changeOrder;
      setSelectedCO(co);
      setCOs((p) => p.map((c) => (c._id === co._id ? co : c)));
      setCoNav("detail", id);
    } catch {
      toast("error", "Failed to load");
    }
  };

  const handleDelete = async (co) => {
    setDeletingId(co._id);
    try {
      await axios.delete(`${API}/changeOrders3/${co._id}`, { headers });
      toast("success", "Deleted");
      setCOs((p) => p.filter((c) => c._id !== co._id));
    } catch (err) {
      toast("error", err.response?.data?.message || err.message);
    } finally {
      setDeletingId(null);
      setConfirmDel(null);
    }
  };

  // ── Filtered + sorted list ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...cos];
    if (statusFilter !== "all")
      list = list.filter((c) => c.status === statusFilter);
    if (billingModeFilter !== "all")
      list = list.filter((c) => resolveBillingMode(c) === billingModeFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(q) ||
          String(c.changeOrderNumber).includes(q) ||
          (c.referenceNumber || "").toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      let av, bv;
      switch (sortField) {
        case "changeOrderNumber":
          av = a.changeOrderNumber ?? 0;
          bv = b.changeOrderNumber ?? 0;
          break;
        case "title":
          av = (a.title || "").toLowerCase();
          bv = (b.title || "").toLowerCase();
          break;
        case "status":
          av = a.status || "";
          bv = b.status || "";
          break;
        case "totalBudgetCost":
          av = a.totalBudgetCost || 0;
          bv = b.totalBudgetCost || 0;
          break;
        case "totalCustomerPrice":
          av = a.totalCustomerPrice || 0;
          bv = b.totalCustomerPrice || 0;
          break;
        case "createdAt":
          av = new Date(a.createdAt || 0).getTime();
          bv = new Date(b.createdAt || 0).getTime();
          break;
        case "approvedAt":
          av = new Date(a.approvedAt || 0).getTime();
          bv = new Date(b.approvedAt || 0).getTime();
          break;
        default:
          av = 0;
          bv = 0;
      }
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [cos, statusFilter, billingModeFilter, search, sortField, sortOrder]);

  // Totals for approved
  const approvedCOs = cos.filter((c) => c.status === "Approved");
  const totals = {
    approved: approvedCOs.length,
    budgetCost: approvedCOs.reduce((s, c) => s + (c.totalBudgetCost || 0), 0),
    customerTotal: approvedCOs.reduce(
      (s, c) => s + (c.totalCustomerPrice || 0),
      0,
    ),
  };

  // Subtotals for visible filtered rows
  const visibleTotals = useMemo(
    () => ({
      budgetCost: filtered.reduce((s, c) => s + (c.totalBudgetCost || 0), 0),
      customerTotal: filtered.reduce(
        (s, c) => s + (c.totalCustomerPrice || 0),
        0,
      ),
    }),
    [filtered],
  );

  // ── Excel export ──────────────────────────────────────────────────────────
  const handleExport = useCallback(() => {
    const rows = filtered.map((co) => {
      const mode = resolveBillingMode(co);
      return {
        "CO #": co.changeOrderNumber,
        Title: co.title || "",
        "Reference #": co.referenceNumber || "",
        Status: co.status || "",
        Billing:
          mode === "separate"
            ? "Separate"
            : mode === "nonBillable"
              ? "No"
              : "Yes",
        Items: (co.items || []).length,
        "Budget Cost": co.totalBudgetCost || 0,
        "Customer Total": co.totalCustomerPrice || 0,
        Created: fmtDate(co.createdAt),
      };
    });

    // Subtotal row
    rows.push({
      "CO #": "",
      Title: "— SUBTOTAL —",
      "Reference #": "",
      Status: "",
      Billing: "",
      Items: "",
      "Budget Cost": visibleTotals.budgetCost,
      "Customer Total": visibleTotals.customerTotal,
      Created: "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    // Column widths
    ws["!cols"] = [
      { wch: 8 },
      { wch: 36 },
      { wch: 16 },
      { wch: 12 },
      { wch: 10 },
      { wch: 8 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    const sheetLabel =
      statusFilter !== "all"
        ? `${statusFilter} COs`
        : billingModeFilter === "billable"
          ? "Billable COs"
          : billingModeFilter === "separate"
            ? "Separate COs"
            : billingModeFilter === "nonBillable"
              ? "Non-Billable COs"
              : "All COs";
    XLSX.utils.book_append_sheet(wb, ws, sheetLabel);

    const projName = (project?.projectName || "Project").replace(
      /[^a-z0-9]/gi,
      "_",
    );
    XLSX.writeFile(wb, `${projName}_change_orders.xlsx`);
  }, [filtered, visibleTotals, statusFilter, billingModeFilter, project]);

  // ── Subviews ──────────────────────────────────────────────────────────────
  if (view === "create")
    return (
      <div className="space-y-5">
        <Toast toasts={toasts} remove={remove} />
        <PageHeader
          onBack={() => setCoNav("list")}
          backLabel="Back"
          title="New Change Order"
        />
        {loadingOpts ? (
          spinner
        ) : (
          <COFormV3
            projectId={projectId}
            token={token}
            purchaseOrders={pos}
            workOrders={wos}
            databaseItems={dbItems}
            budgetItemMap={budgetItemMap}
            companyProfile={companyProfile}
            projectData={project}
            contractLabourRates={contractLabourRates}
            onSaved={async (co) => {
              loadCOs();
              try {
                const r = await axios.get(`${API}/changeOrders3/${co._id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setSelectedCO(r.data.changeOrder);
                setCoNav("detail", r.data.changeOrder._id);
              } catch {
                setSelectedCO(co);
                setCoNav("detail", co._id);
              }
            }}
            onCancel={() => setCoNav("list")}
            toast={toast}
          />
        )}
      </div>
    );

  if (view === "edit" && selectedCO)
    return (
      <div className="space-y-5">
        <Toast toasts={toasts} remove={remove} />
        <PageHeader
          onBack={() => setCoNav("detail", selectedCO._id)}
          backLabel="Detail"
          title={`Edit CO#-${selectedCO.changeOrderNumber} — ${selectedCO.title}`}
        />
        {loadingOpts ? (
          spinner
        ) : (
          <COFormV3
            projectId={projectId}
            token={token}
            purchaseOrders={pos}
            workOrders={wos}
            databaseItems={dbItems}
            budgetItemMap={budgetItemMap}
            companyProfile={companyProfile}
            projectData={project}
            contractLabourRates={contractLabourRates}
            coToEdit={selectedCO}
            onSaved={async (co) => {
              loadCOs();
              try {
                const r = await axios.get(`${API}/changeOrders3/${co._id}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                setSelectedCO(r.data.changeOrder);
              } catch {
                setSelectedCO(co);
              }
              setCoNav("detail", co._id);
            }}
            onCancel={() => setCoNav("detail", selectedCO._id)}
            toast={toast}
          />
        )}
      </div>
    );

  if (view === "detail" && selectedCO)
    return (
      <div>
        <Toast toasts={toasts} remove={remove} />
        <CODetailV3
          co={selectedCO}
          purchaseOrders={pos}
          workOrders={wos}
          databaseItems={dbItems}
          companyProfile={companyProfile}
          projectData={project}
          token={token}
          onBack={async () => {
            setCoNav("list");
            await loadCOs();
          }}
          onUpdated={async (co) => {
            try {
              const r = await axios.get(`${API}/changeOrders3/${co._id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const full = r.data.changeOrder;
              setSelectedCO(full);
              setCOs((p) => p.map((c) => (c._id === full._id ? full : c)));
            } catch {
              setSelectedCO(co);
              setCOs((p) => p.map((c) => (c._id === co._id ? co : c)));
            }
          }}
          onEdit={async () => {
            loadOpts();
            try {
              const r = await axios.get(
                `${API}/changeOrders3/${selectedCO._id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );
              setSelectedCO(r.data.changeOrder);
            } catch {}
            setCoNav("edit", selectedCO._id);
          }}
          toast={toast}
        />
      </div>
    );

  // ── List view ─────────────────────────────────────────────────────────────
  const hasFilters =
    statusFilter !== "all" || billingModeFilter !== "all" || search;

  return (
    <div className="space-y-4">
      <Toast toasts={toasts} remove={remove} />
      <Confirm
        open={!!confirmDel}
        title="Delete Change Order"
        message={`Delete "${confirmDel?.title}"?\n\nThis cannot be undone.`}
        label="Delete"
        color="red"
        onOk={() => handleDelete(confirmDel)}
        onClose={() => setConfirmDel(null)}
      />

      {/* Summary tiles — approved COs only */}
      {cos.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Tile
            label="Approved COs"
            value={String(totals.approved)}
            color="text-green-400"
            str
          />
          <Tile
            label="Approved Budget Cost"
            value={totals.budgetCost}
            color={totals.budgetCost < 0 ? "text-red-400" : "text-green-400"}
          />
          <Tile
            label="Approved Customer Total"
            value={totals.customerTotal}
            color="text-orange-400"
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => {
            loadOpts();
            setCoNav("create");
          }}
          className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors shrink-0"
        >
          + New Change Order
        </button>

        {/* Search */}
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs pointer-events-none">
            ⌕
          </span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, CO#, ref…"
            className="pl-7 pr-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-gray-200 placeholder-gray-600 focus:border-orange-500 focus:outline-none w-52"
          />
        </div>

        {/* Status filter */}
        <div className="flex rounded-lg overflow-hidden border border-gray-700">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={cls(
                "px-3 py-2 text-xs font-medium transition-colors",
                statusFilter === s
                  ? "bg-orange-500 text-white"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-700",
              )}
            >
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>

        {/* Billing mode filter */}
        <div className="flex rounded-lg overflow-hidden border border-gray-700">
          {[
            ["all", "All"],
            ["billable", "Billable"],
            ["separate", "Separate"],
            ["nonBillable", "Non-Bill"],
          ].map(([v, l]) => (
            <button
              key={v}
              onClick={() => setBillingModeFilter(v)}
              className={cls(
                "px-3 py-2 text-xs font-medium transition-colors",
                billingModeFilter === v
                  ? v === "separate"
                    ? "bg-purple-600 text-white"
                    : "bg-orange-500 text-white"
                  : "bg-gray-800 text-gray-400 hover:bg-gray-700",
              )}
            >
              {l}
            </button>
          ))}
        </div>

        {hasFilters && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setBillingModeFilter("all");
              setSearch("");
            }}
            className="px-2.5 py-2 text-xs text-gray-400 hover:text-white bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ✕ Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {filtered.length} of {cos.length} orders
          </span>
          {filtered.length > 0 && (
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              ↓ Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          <span className="ml-3 text-gray-400 text-sm">Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-800 rounded-xl border border-dashed border-gray-700 p-12 text-center">
          <p className="text-2xl mb-3">📋</p>
          <h3 className="text-base font-semibold text-white mb-1">
            {hasFilters ? "No matching change orders" : "No Change Orders Yet"}
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            {!hasFilters &&
              "Create a change order to track scope changes against POs and WOs."}
          </p>
          {!hasFilters && (
            <button
              onClick={() => {
                loadOpts();
                setCoNav("create");
              }}
              className="px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-lg hover:bg-orange-600"
            >
              + New Change Order
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-xl border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/60">
              <thead className="bg-gray-800/80">
                <tr>
                  <Th
                    label="CO #"
                    field="changeOrderNumber"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="pl-5 w-20"
                  />
                  <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap w-36">
                    Ref #
                  </th>
                  <Th
                    label="Title"
                    field="title"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <Th
                    label="Status"
                    field="status"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="w-28"
                  />
                  <Th
                    label="Budget Cost"
                    field="totalBudgetCost"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="w-36 text-right"
                  />
                  <Th
                    label="Customer $"
                    field="totalCustomerPrice"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="w-36 text-right"
                  />
                  <Th
                    label="Created"
                    field="createdAt"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="w-32"
                  />
                  <Th
                    label="Approved On"
                    field="approvedAt"
                    sortField={sortField}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    className="w-32"
                  />
                  <th className="px-4 py-3 w-32" />
                </tr>
              </thead>

              <tbody className="bg-gray-800 divide-y divide-gray-700/40">
                {filtered.map((co) => {
                  const tc = co.totalBudgetCost || 0;
                  const ct = co.totalCustomerPrice || 0;
                  const um = countUnmapped(co.items || []);
                  const missingSec = (co.items || []).filter(
                    (i) => !i.sectionNumber?.trim(),
                  ).length;
                  const mode = resolveBillingMode(co);

                  return (
                    <tr
                      key={co._id}
                      onClick={() => {
                        loadOpts();
                        loadDetail(co._id);
                      }}
                      className="cursor-pointer transition-colors duration-100 hover:bg-gray-700/70 active:bg-gray-700 group"
                    >
                      {/* CO # */}
                      <td className="pl-5 pr-3 py-3.5 text-sm font-mono text-gray-300 whitespace-nowrap">
                        #{co.changeOrderNumber}
                      </td>

                      {/* Ref # */}
                      <td className="px-4 py-3.5 text-sm font-mono text-gray-400 whitespace-nowrap">
                        {co.referenceNumber || (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>

                      {/* Title + badges */}
                      <td className="px-4 py-3.5 max-w-[260px]">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-semibold text-white truncate">
                            {co.title}
                          </span>
                          {mode === "separate" && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-purple-900/40 text-purple-300 rounded border border-purple-700/40 shrink-0">
                              Separate
                            </span>
                          )}
                          {mode === "nonBillable" && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-gray-700 text-gray-400 rounded border border-gray-600 shrink-0">
                              Non-Bill
                            </span>
                          )}
                          {um > 0 && co.status === "Pending" && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-yellow-900/40 text-yellow-400 rounded border border-yellow-700/30 shrink-0">
                              ⚠ {um} unmapped
                            </span>
                          )}
                          {missingSec > 0 && co.status === "Pending" && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-red-900/40 text-red-400 rounded border border-red-700/30 shrink-0">
                              ⛔ {missingSec} no section
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <StatusBadge status={co.status} />
                      </td>

                      {/* Budget cost */}
                      <td
                        className={cls(
                          "px-4 py-3.5 text-sm text-right tabular-nums whitespace-nowrap font-medium",
                          tc < 0 ? "text-red-400" : "text-green-300",
                        )}
                      >
                        {fmtC(tc)}
                      </td>

                      {/* Customer total */}
                      <td className="px-4 py-3.5 text-sm text-right tabular-nums whitespace-nowrap font-medium text-orange-400">
                        {fmtC(ct)}
                      </td>

                      {/* Created date */}
                      <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">
                        {fmtDate(co.createdAt)}
                      </td>

                      {/* Approved On */}
                      <td className="px-4 py-3.5 text-sm whitespace-nowrap">
                        {co.approvedAt ? (
                          <span className="text-green-400">
                            {fmtDate(co.approvedAt)}
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2 justify-end">
                          {(co.status === "Pending" ||
                            co.status === "Rejected") && (
                            <button
                              disabled={deletingId === co._id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDel(co);
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-red-400 bg-red-950/30 border border-red-900/40 rounded-lg hover:bg-red-900/50 disabled:opacity-40 transition-colors opacity-0 group-hover:opacity-100"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>

              {/* Subtotal footer */}
              {filtered.length > 1 && (
                <tfoot className="bg-gray-900 border-t border-gray-600">
                  <tr>
                    <td
                      colSpan={5}
                      className="pl-5 pr-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      Subtotal ({filtered.length} orders)
                    </td>
                    <td
                      className={cls(
                        "px-4 py-3 text-sm font-bold text-right tabular-nums",
                        visibleTotals.budgetCost < 0
                          ? "text-red-400"
                          : "text-green-300",
                      )}
                    >
                      {fmtC(visibleTotals.budgetCost)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-right tabular-nums text-orange-400">
                      {fmtC(visibleTotals.customerTotal)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
